import React, { useState, useEffect } from 'react';
import { workLogAdminAPI} from '../../services/api';
import { toDateTimeLocal, fromDateTimeLocal } from '../../utils/timezone';
import { Clock, Repeat, Trash2, X, AlertCircle } from 'lucide-react';
import './WorkLogEditModal.css';

interface WorkLog {
  id: number;
  startTime: string;
  endTime: string | null;
  description: string | null;
  totalMinutes: number | null;
}

interface WorkLogEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workLog?: WorkLog | null;
  userId: number;
  date: string;
  userName: string;
}

export const WorkLogEditModal: React.FC<WorkLogEditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  workLog,
  userId,
  date,
  userName
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [entryType, setEntryType] = useState<'worklog'>('worklog');
  
  const [workLogFormData, setWorkLogFormData] = useState({
    startTime: '',
    endTime: '',
    description: '',
    applyBreakRules: true
  });

  const [timeOffFormData, setTimeOffFormData] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    type: 'VACATION' as 'VACATION' | 'SICK' | 'SPECIAL_LEAVE'
  });

  const isEditMode = !!workLog;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && workLog) {
        setEntryType('worklog');
        setWorkLogFormData({
          startTime: toDateTimeLocal(workLog.startTime),
          endTime: workLog.endTime ? toDateTimeLocal(workLog.endTime) : '',
          description: workLog.description || '',
          applyBreakRules: true
        });
      } else {
        const defaultStart = `${date}T08:00`;
        const defaultEnd = `${date}T17:00`;
        setWorkLogFormData({
          startTime: defaultStart,
          endTime: defaultEnd,
          description: '',
          applyBreakRules: true
        });
        setTimeOffFormData({
          startDate: date,
          endDate: date,
          reason: '',
          type: 'VACATION'
        });
      }
      setMessage({ type: '', text: '' });
    }
  }, [isOpen, isEditMode, workLog, date]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await handleWorkLogSubmit();
      await onSuccess();

      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || `Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'} des Eintrags`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWorkLogSubmit = async () => {
    if (isEditMode && workLog) {
      // Prüfen ob es sich um Auto-Ausstempel handelt und Zeiten geändert wurden
      const isAutoClockOut = workLog.description && workLog.description.includes('Auto-Ausstempeln');
      const originalStartTime = toDateTimeLocal(workLog.startTime);
      const originalEndTime = workLog.endTime ? toDateTimeLocal(workLog.endTime) : '';
      
      const timesChanged = workLogFormData.startTime !== originalStartTime || 
                          workLogFormData.endTime !== originalEndTime;
      
      // Beschreibung leeren wenn Auto-Ausstempel und Zeiten geändert wurden
      let finalDescription = workLogFormData.description;
      if (isAutoClockOut && timesChanged) {
        finalDescription = '';
      }
      
      // Wenn endTime leer ist, explizit null senden
      const endTimeValue = workLogFormData.endTime ? fromDateTimeLocal(workLogFormData.endTime) : null;
      
      await workLogAdminAPI.updateWorkLog(workLog.id, {
        startTime: fromDateTimeLocal(workLogFormData.startTime),
        endTime: endTimeValue,
        description: finalDescription || "",
        applyBreakRules: workLogFormData.applyBreakRules
      });
      setMessage({ type: 'success', text: 'Arbeitszeit erfolgreich aktualisiert' });
    } else {
      // Bei neuen WorkLogs undefined statt null verwenden wenn leer
      await workLogAdminAPI.createWorkLog({
        userId,
        startTime: fromDateTimeLocal(workLogFormData.startTime),
        endTime: workLogFormData.endTime ? fromDateTimeLocal(workLogFormData.endTime) : undefined,
        description: workLogFormData.description || "",
        applyBreakRules: workLogFormData.applyBreakRules
      });
      setMessage({ type: 'success', text: 'Arbeitszeit erfolgreich erstellt' });
    }
  };

  const handleWorkLogChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setWorkLogFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleTimeOffChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTimeOffFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDelete = async () => {
    if (!isEditMode || !workLog) return;

    if (!confirm(`Arbeitszeit vom ${new Date(workLog.startTime).toLocaleDateString('de-DE')} wirklich löschen?`)) {
      return;
    }

    setLoading(true);
    try {
      await workLogAdminAPI.deleteWorkLog(workLog.id);
      setMessage({ type: 'success', text: 'Arbeitszeit erfolgreich gelöscht' });
      
      await onSuccess();
      
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Löschen der Arbeitszeit'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSwapTimes = () => {
    const temp = workLogFormData.startTime;
    setWorkLogFormData(prev => ({
      ...prev,
      startTime: prev.endTime,
      endTime: temp
    }));
  };

  const handleClearEndTime = () => {
    if (!confirm('Möchten Sie die Endzeit wirklich löschen? Der Benutzer wird als "aktuell arbeitend" markiert.')) {
      return;
    }
    setWorkLogFormData(prev => ({
      ...prev,
      endTime: ''
    }));
  };

  const getEntryTypeLabel = () => {
    switch (entryType) {
      case 'worklog': return 'Arbeitszeit';
      default: return 'Eintrag';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content worklog-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {isEditMode ? (
              <>
                <Clock size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Arbeitszeit bearbeiten
              </>
            ) : (
              <>
                <Clock size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                {getEntryTypeLabel()} hinzufügen
              </>
            )}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'error' && <AlertCircle size={16} style={{ marginRight: '8px' }} />}
            {message.text}
            {message.type === 'success' && (
              <button 
                className="message-close" 
                onClick={() => setMessage({ type: '', text: '' })}
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}

        <div className="modal-body">
          <div className="form-info">
            <p><strong>Mitarbeiter:</strong> {userName}</p>
            <p><strong>Datum:</strong> {new Date(date).toLocaleDateString('de-DE', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</p>
            {isEditMode && workLog && (
              <p><strong>ID:</strong> {workLog.id}</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="worklog-form">
            {entryType === 'worklog' ? (
              <>
                <div className="form-row time-inputs-row">
                  <div className="form-group">
                    <label>Startzeit:</label>
                    <input
                      type="datetime-local"
                      name="startTime"
                      value={workLogFormData.startTime}
                      onChange={handleWorkLogChange}
                      required
                    />
                  </div>
                  
                  {isEditMode && workLogFormData.startTime && workLogFormData.endTime && (
                    <div className="time-swap-container">
                      <button
                        type="button"
                        className="btn-swap"
                        onClick={handleSwapTimes}
                        title="Start- und Endzeit tauschen"
                      >
                        <Repeat size={20} />
                      </button>
                    </div>
                  )}
                  
                  <div className="form-group endtime-group">
                    <label>Endzeit:</label>
                    <div className="endtime-input-wrapper">
                      <input
                        type="datetime-local"
                        name="endTime"
                        value={workLogFormData.endTime}
                        onChange={handleWorkLogChange}
                      />
                      {isEditMode && workLogFormData.endTime && (
                        <button
                          type="button"
                          className="btn-clear-time"
                          onClick={handleClearEndTime}
                          title="Endzeit löschen (Benutzer als arbeitend markieren)"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                    <small>Leer lassen für laufende Arbeitszeit</small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Beschreibung:</label>
                  <textarea
                    name="description"
                    value={workLogFormData.description}
                    onChange={handleWorkLogChange}
                    placeholder="Beschreibung der Tätigkeit..."
                    rows={3}
                  />
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="applyBreakRules"
                      checked={workLogFormData.applyBreakRules}
                      onChange={handleWorkLogChange}
                    />
                    Pausenregeln automatisch anwenden
                  </label>
                </div>
              </>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Startdatum:</label>
                    <input
                      type="date"
                      name="startDate"
                      value={timeOffFormData.startDate}
                      onChange={handleTimeOffChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Enddatum:</label>
                    <input
                      type="date"
                      name="endDate"
                      value={timeOffFormData.endDate}
                      onChange={handleTimeOffChange}
                      min={timeOffFormData.startDate}
                      required
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="form-actions">
              <button type="submit" className="btn success" disabled={loading}>
                {loading ? 
                  (isEditMode ? 'Speichern...' : 'Erstellen...') : 
                  (isEditMode ? 'Speichern' : 'Erstellen')
                }
              </button>
              
              {isEditMode && entryType === 'worklog' && (
                <button 
                  type="button" 
                  className="btn danger" 
                  onClick={handleDelete}
                  disabled={loading}
                >
                  <Trash2 size={16} style={{ marginRight: '4px' }} />
                  Löschen
                </button>
              )}
              
              <button type="button" className="btn secondary" onClick={onClose}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
